import React, { useState, useEffect } from 'react'
import { Container, Row, Col, Card, CardBody, Form, FormGroup, Label, Input, Button, Alert } from 'reactstrap'
import { Breadcrumbs } from '../../AbstractElements'
import HeaderCard from '../Common/Component/HeaderCard'
import { getIdData } from '../../lib/studentinfo'
import { updateData } from '../../lib/profiles'
import { useNavigate, useParams } from 'react-router-dom'
import { useLayout } from '../../Hooks/useLayout'
import { useRoleBasedUrl } from '../../Hooks/useRoleBasedUrl'
import { ClassForLessonSelect } from '../Common/FormSelects'

const AdminStudentEdit = () => {
	const [formData, setFormData] = useState({
		name: '',
		surname: '',
		isActive: true,
		phone: '',
		username: '',
		class: 9,
		studentschooltext: '',
		schoollink: '',
		graduateyear: '',
		ranking: '',
		gpa: '',
		satpsat: '',
		statetest: '',
		major: '',
		career: '',
		fiveAdjectives: '',
		hsSchoolStatistics: '',
		topThreeColleges: '',
		lor: '',
		activities: '',
		collegeEssay: '',
		instructorNote: '',
		studentNote: '',
		meetlink: '',
		folderlink: '',
		congawardlink: '',
		satlink: '',
		awardlink: '',
		calendarlink: '',
		courselink: '',
		visitlink: '',
		deadlinelink: '',
		finaidlink: '',
		mainessaylink: '',
		supplementallink: ''
	})
	const [loading, setLoading] = useState(false)
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState('')

	const navigate = useNavigate()
	const { id, type } = useParams()
	const layout = useLayout()
	const role = useRoleBasedUrl()

	const statusOptions = [
		{ value: true, label: 'Active' },
		{ value: false, label: 'Inactive' }
	]

	const handleInputChange = (field, value) => {
		setFormData((prev) => ({
			...prev,
			[field]: value
		}))
	}

	const handleSubmit = async (e) => {
		e.preventDefault()
		setSaving(true)
		setError('')

		try {
			await updateData({
				id: id,
				username: formData.username,
				isActive: formData.isActive,
				name: formData.name,
				surname: formData.surname,
				phone: formData.phone,
				class: formData.class,
				studentschooltext: formData.studentschooltext,
				schoollink: formData.schoollink,
				graduateyear: formData.graduateyear,
				ranking: formData.ranking,
				gpa: formData.gpa,
				satpsat: formData.satpsat,
				statetest: formData.statetest,
				major: formData.major,
				career: formData.career,
				fiveAdjectives: formData.fiveAdjectives,
				hsSchoolStatistics: formData.hsSchoolStatistics,
				topThreeColleges: formData.topThreeColleges,
				lor: formData.lor,
				activities: formData.activities,
				collegeEssay: formData.collegeEssay,
				instructorNote: formData.instructorNote,
				studentNote: formData.studentNote,
				meetlink: formData.meetlink,
				folderlink: formData.folderlink,
				congawardlink: formData.congawardlink,
				satlink: formData.satlink,
				awardlink: formData.awardlink,
				calendarlink: formData.calendarlink,
				courselink: formData.courselink,
				visitlink: formData.visitlink,
				deadlinelink: formData.deadlinelink,
				finaidlink: formData.finaidlink,
				mainessaylink: formData.mainessaylink,
				supplementallink: formData.supplementallink
			})

			if (type === 'Add') {
				// After creating student, navigate to student detail page
				navigate(`${process.env.PUBLIC_URL}/${role}/students/detail/${id}/${layout}`)
			} else {
				// After updating student, navigate to student list page
				navigate(`${process.env.PUBLIC_URL}/${role}/students/${layout}`)
			}
		} catch (error) {
			setError('Failed to save student information: ' + error.message)
		} finally {
			setSaving(false)
		}
	}

	const fetchStudentData = async () => {
		if (type === 'Update') {
			try {
				setLoading(true)
				const info = await getIdData(id)
				if (info && info[0]) {
					const student = info[0]
					setFormData({
						name: student.name || '',
						surname: student.surname || '',
						isActive: student.isActive !== undefined ? student.isActive : true,
						phone: student.phone || '',
						username: student.username || '',
						class: student.class || 9,
						studentschooltext: student.studentschooltext || student.schoolname || '',
						schoollink: student.schoollink || '',
						graduateyear: student.graduateyear || '',
						ranking: student.ranking || '',
						gpa: student.gpa || '',
						satpsat: student.satpsat || '',
						statetest: student.statetest || '',
						major: student.major || '',
						career: student.career || '',
						fiveAdjectives: student.fiveAdjectives || '',
						hsSchoolStatistics: student.hsSchoolStatistics || '',
						topThreeColleges: student.topThreeColleges || '',
						lor: student.lor || '',
						activities: student.activities || '',
						collegeEssay: student.collegeEssay || '',
						instructorNote: student.instructorNote || '',
						studentNote: student.studentNote || '',
						meetlink: student.meetlink || '',
						folderlink: student.folderlink || '',
						congawardlink: student.congawardlink || '',
						satlink: student.satlink || '',
						awardlink: student.awardlink || '',
						calendarlink: student.calendarlink || '',
						courselink: student.courselink || '',
						visitlink: student.visitlink || '',
						deadlinelink: student.deadlinelink || '',
						finaidlink: student.finaidlink || '',
						mainessaylink: student.mainessaylink || '',
						supplementallink: student.supplementallink || ''
					})
				}
			} catch (error) {
				setError('Failed to load student information: ' + error.message)
			} finally {
				setLoading(false)
			}
		}
	}

	useEffect(() => {
		fetchStudentData()
	}, [id, type])

	if (loading) {
		return (
			<Container fluid={true}>
				<Row>
					<Col sm="12">
						<Card>
							<CardBody className="text-center">
								<div className="spinner-border" role="status">
									<span className="visually-hidden">Loading...</span>
								</div>
								<p className="mt-2">Loading student information...</p>
							</CardBody>
						</Card>
					</Col>
				</Row>
			</Container>
		)
	}

	return (
		<>
			<Breadcrumbs
				parent="Student Management"
				title={type === 'Add' ? 'Add Student' : 'Edit Student'}
				mainTitle={type === 'Add' ? 'Add New Student' : 'Edit Student Information'}
			/>
			<Container fluid={true}>
				<Row>
					<Col sm="12">
						<Card>
							<HeaderCard
								title={type === 'Add' ? 'Add Student' : 'Edit Student'}
								subTitle={type === 'Add' ? 'Complete student information' : 'Update student information'}
							/>
							<CardBody>
								{error && (
									<Alert color="danger" className="mb-3">
										{error}
									</Alert>
								)}

								<Form onSubmit={handleSubmit}>
									<Row>
										<Col md="6">
											<FormGroup>
												<Label for="name">First Name *</Label>
												<Input
													id="name"
													type="text"
													value={formData.name}
													onChange={(e) => handleInputChange('name', e.target.value)}
													placeholder="Enter first name"
													required
												/>
											</FormGroup>
										</Col>
										<Col md="6">
											<FormGroup>
												<Label for="surname">Last Name *</Label>
												<Input
													id="surname"
													type="text"
													value={formData.surname}
													onChange={(e) => handleInputChange('surname', e.target.value)}
													placeholder="Enter last name"
													required
												/>
											</FormGroup>
										</Col>
									</Row>

									<Row>
										<Col md="4">
											<FormGroup>
												<Label for="username">Username</Label>
												<Input
													id="username"
													type="text"
													value={formData.username}
													onChange={(e) => handleInputChange('username', e.target.value)}
													placeholder="Enter username"
												/>
											</FormGroup>
										</Col>
										<Col md="4">
											<FormGroup>
												<Label for="isActive">Status</Label>
												<Input
													id="isActive"
													type="select"
													value={formData.isActive}
													onChange={(e) => handleInputChange('isActive', e.target.value === 'true')}
												>
													{statusOptions.map((option) => (
														<option key={option.value} value={option.value}>
															{option.label}
														</option>
													))}
												</Input>
											</FormGroup>
										</Col>
										<Col md="4">
											<FormGroup>
												<Label for="phone">Phone</Label>
												<Input
													id="phone"
													type="tel"
													value={formData.phone}
													onChange={(e) => handleInputChange('phone', e.target.value)}
													placeholder="123-456-7890"
												/>
											</FormGroup>
										</Col>
									</Row>

									<Row>
										<Col md="6">
											<FormGroup>
												<Label for="studentschooltext">School</Label>
												<Input
													id="studentschooltext"
													type="text"
													value={formData.studentschooltext}
													onChange={(e) => handleInputChange('studentschooltext', e.target.value)}
													placeholder="Enter school name"
												/>
											</FormGroup>
										</Col>
										<Col md="6">
											<FormGroup>
												<Label for="schoollink">School Link</Label>
												<Input
													id="schoollink"
													type="text"
													value={formData.schoollink}
													onChange={(e) => handleInputChange('schoollink', e.target.value)}
													placeholder="School website URL"
												/>
											</FormGroup>
										</Col>
									</Row>

									<Row>
										<Col md="6">
											<FormGroup>
												<ClassForLessonSelect
													value={formData.class}
													setValue={(value) => handleInputChange('class', value)}
													name="ClassForLessonSelect"
												/>
											</FormGroup>
										</Col>
										<Col md="6">
											<FormGroup>
												<Label for="graduateyear">Graduation Year</Label>
												<Input
													id="graduateyear"
													type="text"
													value={formData.graduateyear}
													onChange={(e) => handleInputChange('graduateyear', e.target.value)}
													placeholder="Enter graduation year"
												/>
											</FormGroup>
										</Col>
									</Row>

									<Row>
									<Col md="6">
											<FormGroup>
												<Label for="ranking">Ranking</Label>
												<Input
													id="ranking"
													type="text"
													value={formData.ranking}
													onChange={(e) => handleInputChange('ranking', e.target.value)}
													placeholder="Enter ranking"
												/>
											</FormGroup>
										</Col>
										<Col md="6">
											<FormGroup>
												<Label for="gpa">GPA: Weighted and unweighted GPA if known</Label>
												<Input
													id="gpa"
													type="text"
													value={formData.gpa}
													onChange={(e) => handleInputChange('gpa', e.target.value)}
													placeholder="Enter GPA: Weighted and unweighted GPA if known"
												/>
											</FormGroup>
										</Col>
									</Row>

									<Row>
										<Col md="12">
											<FormGroup>
												<Label for="satpsat">SAT and or PSAT scores and the date of testing</Label>
												<Input
													id="satpsat"
													type="textarea"
													value={formData.satpsat}
													onChange={(e) => handleInputChange('satpsat', e.target.value)}
													placeholder="Enter SAT and or PSAT scores and the date of testing"
													rows="3"
												/>
											</FormGroup>
										</Col>
									</Row>

									<Row>
										<Col md="12">
											<FormGroup>
												<Label for="statetest">Practice SAT: Official practice SAT score and the date of testing</Label>
												<Input
													id="statetest"
													type="textarea"
													value={formData.statetest}
													onChange={(e) => handleInputChange('statetest', e.target.value)}
													placeholder="Enter Practice SAT: Official practice SAT score and the date of testing"
													rows="3"
												/>
											</FormGroup>
										</Col>
									</Row>

									<Row>
										<Col md="12">
											<FormGroup>
												<Label for="hsSchoolStatistics">About my high school</Label>
												<Input
													id="hsSchoolStatistics"
													type="text"
													value={formData.hsSchoolStatistics}
													onChange={(e) => handleInputChange('hsSchoolStatistics', e.target.value)}
													placeholder="About my high school"
												/>
											</FormGroup>
										</Col>
									</Row>

									<Row>
										<Col md="12">
											<FormGroup>
												<Label for="fiveAdjectives">Five Adjectives:five character trait adjectives that describe you the most</Label>
												<Input
													id="fiveAdjectives"
													type="text"
													value={formData.fiveAdjectives}
													onChange={(e) => handleInputChange('fiveAdjectives', e.target.value)}
													placeholder="Enter Five Adjectives:five character trait adjectives that describe you the most"
												/>
											</FormGroup>
										</Col>
									</Row>

									<Row>
										<Col md="12">
											<FormGroup>
												<Label for="major">Major: What do you want to study in college?</Label>
												<Input
													id="major"
													type="text"
													value={formData.major}
													onChange={(e) => handleInputChange('major', e.target.value)}
													placeholder="Enter Major: What do you want to study in college?"
												/>
											</FormGroup>
										</Col>
									</Row>

									<Row>
										<Col md="12">
											<FormGroup>
												<Label for="career">Career: What do you want to do after college?</Label>
												<Input
													id="career"
													type="text"
													value={formData.career}
													onChange={(e) => handleInputChange('career', e.target.value)}
													placeholder="Enter Career: What do you want to do after college?"
												/>
											</FormGroup>
										</Col>
									</Row>

									<Row>
										<Col md="12">
											<FormGroup>
												<Label for="topThreeColleges">Desired Colleges Top three college choices</Label>
												<Input
													id="topThreeColleges"
													type="text"
													value={formData.topThreeColleges}
													onChange={(e) => handleInputChange('topThreeColleges', e.target.value)}
													placeholder="Enter Desired Colleges Top three college choices"
												/>
											</FormGroup>
										</Col>
									</Row>

									<Row>
										<Col md="12">
											<FormGroup>
												<Label for="lor">Letter of Recommendation: Did you get one? It so, which teacher wrote it?</Label>
												<Input
													id="lor"
													type="text"
													value={formData.lor}
													onChange={(e) => handleInputChange('lor', e.target.value)}
													placeholder="Enter Letter of Recommendation: Did you get one? It so, which teacher wrote it?"
												/>
											</FormGroup>
										</Col>
									</Row>

									<Row>
										<Col md="12">
											<FormGroup>
												<Label for="activities">Activities: List all activities outside of your high school hours</Label>
												<Input
													id="activities"
													type="textarea"
													value={formData.activities}
													onChange={(e) => handleInputChange('activities', e.target.value)}
													placeholder="Enter Activities: List all activities outside of your high school hours"
													rows="3"
												/>
											</FormGroup>
										</Col>
									</Row>

									<Row>
										<Col md="12">
											<FormGroup>
												<Label for="collegeEssay">College Essay: What progress have you made on your essay so far?</Label>
												<Input
													id="collegeEssay"
													type="textarea"
													value={formData.collegeEssay}
													onChange={(e) => handleInputChange('collegeEssay', e.target.value)}
													placeholder="Enter College Essay: What progress have you made on your essay so far?"
													rows="3"
												/>
											</FormGroup>
										</Col>
									</Row>

									<Row>
										<Col md="12">
											<FormGroup>
												<Label for="instructorNote">Counselor's Comment</Label>
												<Input
													id="instructorNote"
													type="textarea"
													value={formData.instructorNote}
													onChange={(e) => handleInputChange('instructorNote', e.target.value)}
													placeholder="Enter Counselor's Comment"
													rows="3"
												/>
											</FormGroup>
										</Col>
									</Row>

									<Row>
										<Col md="12">
											<FormGroup>
												<Label for="studentNote">Student's Comment</Label>
												<Input
													id="studentNote"
													type="textarea"
													value={formData.studentNote}
													onChange={(e) => handleInputChange('studentNote', e.target.value)}
													placeholder="Enter  Student's Comment"
													rows="3"
												/>
											</FormGroup>
										</Col>
									</Row>

									<Row>
										<Col md="6">
											<FormGroup>
												<Label for="meetlink">Zoom Link</Label>
												<Input
													id="meetlink"
													type="text"
													value={formData.meetlink}
													onChange={(e) => handleInputChange('meetlink', e.target.value)}
													placeholder="Enter Zoom Link"
												/>
											</FormGroup>
										</Col>

										<Col md="6">
											<FormGroup>
												<Label for="folderlink">Folder Link</Label>
												<Input
													id="folderlink"
													type="text"
													value={formData.folderlink}
													onChange={(e) => handleInputChange('folderlink', e.target.value)}
													placeholder="Enter Folder Link"
												/>
											</FormGroup>
										</Col>
									</Row>

									<Row>
										<Col md="6">
											<FormGroup>
												<Label for="congawardlink">Congressional Award Link</Label>
												<Input
													id="congawardlink"
													type="text"
													value={formData.congawardlink}
													onChange={(e) => handleInputChange('congawardlink', e.target.value)}
													placeholder="Enter Congressional Award Link"
												/>
											</FormGroup>
										</Col>
										<Col md="6">
											<FormGroup>
												<Label for="satlink">SAT Link</Label>
												<Input
													id="satlink"
													type="text"
													value={formData.satlink}
													onChange={(e) => handleInputChange('satlink', e.target.value)}
													placeholder="Enter SAT Link"
												/>
											</FormGroup>
										</Col>
									</Row>

									<Row>
										<Col md="6">
											<FormGroup>
												<Label for="awardlink">Awards Link</Label>
												<Input
													id="awardlink"
													type="text"
													value={formData.awardlink}
													onChange={(e) => handleInputChange('awardlink', e.target.value)}
													placeholder="Enter Awards Link"
												/>
											</FormGroup>
										</Col>
										<Col md="6">
											<FormGroup>
												<Label for="calendarlink">Calendar Link</Label>
												<Input
													id="calendarlink"
													type="text"
													value={formData.calendarlink}
													onChange={(e) => handleInputChange('calendarlink', e.target.value)}
													placeholder="Enter Calendar Link"
												/>
											</FormGroup>
										</Col>
									</Row>

									<Row>
										<Col md="6">
											<FormGroup>
												<Label for="courselink">College Courses Link</Label>
												<Input
													id="courselink"
													type="text"
													value={formData.courselink}
													onChange={(e) => handleInputChange('courselink', e.target.value)}
													placeholder="Enter College Courses Link"
												/>
											</FormGroup>
										</Col>
										<Col md="6">
											<FormGroup>
												<Label for="visitlink">College Visits Link</Label>
												<Input
													id="visitlink"
													type="text"
													value={formData.visitlink}
													onChange={(e) => handleInputChange('visitlink', e.target.value)}
													placeholder="Enter College Visits Link"
												/>
											</FormGroup>
										</Col>
									</Row>

									<Row>
										<Col md="6">
											<FormGroup>
												<Label for="deadlinelink">Deadline Link</Label>
												<Input
													id="deadlinelink"
													type="text"
													value={formData.deadlinelink}
													onChange={(e) => handleInputChange('deadlinelink', e.target.value)}
													placeholder="Enter Deadline Link"
												/>
											</FormGroup>
										</Col>
										<Col md="6">
											<FormGroup>
												<Label for="finaidlink">Financial Aid Link</Label>
												<Input
													id="finaidlink"
													type="text"
													value={formData.finaidlink}
													onChange={(e) => handleInputChange('finaidlink', e.target.value)}
													placeholder="Enter Financial Aid Link"
												/>
											</FormGroup>
										</Col>
									</Row>

									<Row>
										<Col md="6">
											<FormGroup>
												<Label for="mainessaylink">Main Essay Link</Label>
												<Input
													id="mainessaylink"
													type="text"
													value={formData.mainessaylink}
													onChange={(e) => handleInputChange('mainessaylink', e.target.value)}
													placeholder="Enter Main Essay Link"
												/>
											</FormGroup>
										</Col>
										<Col md="6">
											<FormGroup>
												<Label for="supplementallink">Supplementals Link</Label>
												<Input
													id="supplementallink"
													type="text"
													value={formData.supplementallink}
													onChange={(e) => handleInputChange('supplementallink', e.target.value)}
													placeholder="Enter Supplementals Link"
												/>
											</FormGroup>
										</Col>
									</Row>

									<div className="d-flex justify-content-between mt-4">
										<Button type="submit" color="primary" disabled={saving}>
											{saving ? 'Saving...' : type === 'Add' ? 'Create Student' : 'Update Student'}
										</Button>
										<Button
											type="button"
											color="secondary"
											onClick={() => {
												if (type === 'Add') {
													// If adding new student, cancel goes to student list
													navigate(`${process.env.PUBLIC_URL}/${role}/students/${layout}`)
												} else {
													// If updating student, cancel goes to student detail
													navigate(`${process.env.PUBLIC_URL}/${role}/students/detail/${id}/${layout}`)
												}
											}}
											disabled={saving}
										>
											Cancel
										</Button>
									</div>
								</Form>
							</CardBody>
						</Card>
					</Col>
				</Row>
			</Container>
		</>
	)
}

export { AdminStudentEdit }
